import { ReactNode, useMemo } from "react";
import { TDH_FITTING_FIELDS } from "../../../data/tdh";
import { PIPE_DIAMETERS } from "../../../data/gpm";
import { TdhHelpBase } from "../../../types/types";
import { RadioGroup } from "../../shared/RadioGroup";
import { SelectField } from "../../shared/SelectField";
import { TextField } from "../../shared/TextField";

type TdhHelpFieldsProps = {
	help: TdhHelpBase;
	onUpdate: (updates: Partial<TdhHelpBase>) => void;
	staticHeadLabel?: string;
	pipeDiameterLabel?: string;
	pipeMaterialLabel?: string;
	horizontalLengthLabel?: string;
	middleContent?: ReactNode;
};

export function TdhHelpFields({
	help,
	onUpdate,
	staticHeadLabel = "Static Head (ft)",
	pipeDiameterLabel = "Pipe Diameter (in)",
	pipeMaterialLabel = "Pipe Material",
	horizontalLengthLabel = "Horizontal Length of Pipe (ft)*",
	middleContent = null,
}: TdhHelpFieldsProps) {
	const fittingColumns = useMemo(() => {
		const midpoint = Math.ceil(TDH_FITTING_FIELDS.length / 2);
		return [
			TDH_FITTING_FIELDS.slice(0, midpoint),
			TDH_FITTING_FIELDS.slice(midpoint),
		];
	}, []);

	return (
		<div className="pf-tdh-help">
			<TextField
				label={staticHeadLabel}
				type="number"
				value={help.staticHead}
				onChange={(event) => onUpdate({ staticHead: event.target.value })}
				min="0"
				step="any"
				className="pf-field-group--medium"
			/>

			<SelectField
				label={pipeDiameterLabel}
				options={PIPE_DIAMETERS.map((diameter) => ({
					value: diameter,
					label: diameter,
				}))}
				value={help.pipeDiameter}
				onChange={(event) => onUpdate({ pipeDiameter: event.target.value })}
				placeholderDisabled
				className="pf-field-group--medium"
			/>

			<RadioGroup
				label={pipeMaterialLabel}
				name="tdhPipeMaterial"
				value={help.pipeMaterial}
				onChange={(value) =>
					onUpdate({
						pipeMaterial: value as typeof help.pipeMaterial,
					})
				}
				options={[
					{ value: "plastic", label: "Plastic" },
					{ value: "iron", label: "Iron" },
				]}
			/>

			{middleContent}

			<TextField
				label={horizontalLengthLabel}
				type="number"
				value={help.horizontalLength}
				onChange={(event) =>
					onUpdate({ horizontalLength: event.target.value })
				}
				min="0"
				step="any"
				className="pf-field-group--medium"
			/>

			<div className="pf-tdh-help__section">
				<div className="pf-gpm-help__fields">
					{fittingColumns.map((column, columnIndex) => (
						<div key={columnIndex} className="pf-tdh-help__field-column">
							{column.map((fitting) => (
								<TextField
									key={fitting.id}
									label={fitting.label}
									type="number"
									value={help.fittings[fitting.id]}
									onChange={(event) =>
										onUpdate({
											fittings: {
												...help.fittings,
												[fitting.id]: event.target.value,
											},
										})
									}
									min="0"
									step="1"
								/>
							))}
						</div>
					))}
				</div>
			</div>

			<TextField
				label="Total Length of Pipe (ft)"
				type="number"
				value={help.totalLengthOfPipe}
				readOnly
				className="pf-field-group--medium"
				fieldClassName="pf-input--readonly"
			/>
		</div>
	);
}
